import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Lock, Pencil, Trash2, Loader2, KeyRound, Copy, ExternalLink, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ACCESS_COLORS, colorBg } from "@/lib/colors";

const empty = { platform: "", username: "", password: "", url: "", notes: "", color: null };

export function AccessManager({ scope = "own", clientId = null }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // reveal (pin) flow
  const [pinFor, setPinFor] = useState(null); // access item awaiting pin (mode: 'view' | 'edit')
  const [pin, setPin] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(null); // full credentials to show
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = scope === "client" ? { scope, client_id: clientId } : { scope };
      const res = await api.get("/accesses", { params });
      setList(res.data);
    } finally {
      setLoading(false);
    }
  }, [scope, clientId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditingId(null); setFormOpen(true); };

  const save = async () => {
    if (!form.platform.trim()) { toast.error("El nombre de la plataforma es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form, scope, client_id: scope === "client" ? clientId : null };
      if (editingId) await api.put(`/accesses/${editingId}`, payload);
      else await api.post("/accesses", payload);
      toast.success(editingId ? "Acceso actualizado" : "Acceso creado");
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const askPin = (item, mode) => { setPinFor({ item, mode }); setPin(""); };

  const submitPin = async () => {
    setRevealing(true);
    try {
      const res = await api.post(`/accesses/${pinFor.item.id}/reveal`, { pin });
      if (pinFor.mode === "edit") {
        setForm({ platform: res.data.platform, username: res.data.username || "",
          password: res.data.password || "", url: res.data.url || "", notes: res.data.notes || "",
          color: res.data.color || null });
        setEditingId(pinFor.item.id);
        setFormOpen(true);
      } else {
        setRevealed(res.data);
      }
      setPinFor(null);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Clave incorrecta" : "No se pudo abrir");
    } finally {
      setRevealing(false);
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/accesses/${item.id}`);
      toast.success("Acceso eliminado");
      load();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text || "");
    toast.success(`${label} copiado`);
  };

  return (
    <div data-testid={`access-manager-${scope}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Accesos</h2>
        </div>
        <Button size="sm" onClick={openNew} data-testid="new-access-button">
          <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Nuevo acceso</span>
        </Button>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar acceso…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-9" data-testid="access-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-muted-foreground text-sm">
          No hay accesos guardados.
        </div>
      ) : (
        <div className="space-y-2" data-testid="access-list">
          {list.filter((a) =>
            a.platform.toLowerCase().includes(search.toLowerCase()) ||
            (a.url || "").toLowerCase().includes(search.toLowerCase())
          ).map((a) => (
            <div key={a.id} data-testid={`access-item-${a.id}`}
              style={{ backgroundColor: colorBg(ACCESS_COLORS, a.color) }}
              className="flex items-center justify-between gap-3 rounded-md border border-border p-4">
              <button onClick={() => askPin(a, "view")} data-testid={`access-open-${a.id}`}
                className="flex items-center gap-2 min-w-0 text-left">
                <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.platform}</p>
                  {a.url && <p className="text-xs text-muted-foreground truncate">{a.url}</p>}
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => askPin(a, "edit")} data-testid={`access-edit-${a.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(a)} data-testid={`access-delete-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PIN dialog */}
      <Dialog open={!!pinFor} onOpenChange={(o) => !o && setPinFor(null)}>
        <DialogContent data-testid="access-pin-dialog">
          <DialogHeader><DialogTitle>Introduce la clave de seguridad</DialogTitle></DialogHeader>
          <Input type="password" value={pin} placeholder="Clave"
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitPin()}
            data-testid="access-pin-input" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinFor(null)}>Cancelar</Button>
            <Button onClick={submitPin} disabled={revealing} data-testid="access-pin-submit">
              {revealing && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Acceder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revealed credentials */}
      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent data-testid="access-reveal-dialog">
          <DialogHeader><DialogTitle>{revealed?.platform}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {revealed?.url && (
              <div>
                <Label>Plataforma / URL</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={revealed.url} />
                  <Button variant="outline" size="icon" onClick={() => window.open(revealed.url.startsWith("http") ? revealed.url : `https://${revealed.url}`, "_blank")}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <div>
              <Label>Usuario</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={revealed?.username || ""} data-testid="reveal-username" />
                <Button variant="outline" size="icon" onClick={() => copy(revealed?.username, "Usuario")}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Contraseña</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={revealed?.password || ""} data-testid="reveal-password" />
                <Button variant="outline" size="icon" onClick={() => copy(revealed?.password, "Contraseña")}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            {revealed?.notes && (
              <div>
                <Label>Notas</Label>
                <Textarea readOnly value={revealed.notes} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / edit form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="access-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Editar acceso" : "Nuevo acceso"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Plataforma *</Label>
              <Input value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
                data-testid="access-platform-input" placeholder="CCTV Dahua, Router, Panel..." />
            </div>
            <div>
              <Label>URL (opcional)</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                data-testid="access-url-input" placeholder="https://..." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Usuario</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  data-testid="access-username-input" />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="access-password-input" />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="access-notes-input" />
            </div>
            <div>
              <Label>Color (opcional)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ACCESS_COLORS.map((c) => (
                  <button key={c.key || "none"} type="button" onClick={() => setForm({ ...form, color: c.key })}
                    title={c.label} data-testid={`access-color-${c.key || "none"}`}
                    className={`h-7 w-7 rounded-full border-2 transition-transform ${form.color === c.key ? "border-primary scale-110" : "border-border"}`}
                    style={{ backgroundColor: c.bg }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-access-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
